import {map, min} from 'lodash';
import numeral from 'numeraljs';
import PropTypes from 'prop-types';
import {useMemo, useState} from 'react';
import Measure from 'react-measure';

import './Legend.scss';
import ChartConstants from 'components/Chart/Constants';
import {getFontSize, getTextWidthInEm} from 'utils/browserUtils';


const propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    color: PropTypes.string,
  })),
};

/**
 * Renders legend.
 *
 * @param {object} props - props container
 * @param {Array} props.data - array of chart's data, label and color
 * @return {JSX.Element}
 */
export default function ChartLegend({data}) {
  const maxLabel = useMemo(() => {
    let longestLabel = '';
    for (let x = 0; x < data.length; x += 1) {
      const currentLabel = `${data[x].label}: ${numeral(data[x].value).format('0,0')}`;
      if (currentLabel.length > longestLabel.length) {
        longestLabel = currentLabel;
      }
    }
    return longestLabel;
  }, [data]);

  const [chartProps, setChartProps] = useState(calculateChartProps(maxLabel, data));
  
  const onResize = (contentRect) => {
    if (chartProps.width !== contentRect.bounds.width) {
      setChartProps(calculateChartProps(maxLabel, data, contentRect.bounds.width));
    }
  };

  return (
    <Measure bounds={true} onResize={onResize}>
      {({measureRef}) => (
        <div ref={measureRef}>
          <div className="legend" style={chartProps.legendStyle}>
            {
              map(data, (d, i) => {
                const color = d.color || ChartConstants.colors[min([i, data.length - 1])];

                return (
                  <div key={`${i}+${d}`} className="legend__item" style={{flexBasis: `${chartProps.itemWidth}rem`}}>
                    <span className="legend__item__color" style={{backgroundColor: `${color}`}}>&nbsp;</span>

                    <span className="legend__item__label">{d.label}: {numeral(d.value).format('0,0')}</span>
                  </div>
                );
              })
            }
          </div>
        </div>
      )}
    </Measure>
  );
}
ChartLegend.propTypes = propTypes;


/**
 * Calculates chart props.
 *
 * @param {string} maxLabel - longest label in data
 * @param {number} data - array of chart's data, label and color
 * @param {number} width - width of parent container
 */
function calculateChartProps(maxLabel, data, width = 0) {
  const fontSize = getFontSize();
  // pad label with by 2em to account for color box + gap
  const itemWidth = getTextWidthInEm(maxLabel, fontSize) + 2.2;
  const legendStyle = getLegendWidth(width, itemWidth, fontSize, data);
  return {
    itemWidth,
    legendStyle,
  };
}

// legend flexbox has a gap of 1.5em between items
const gapWidth = 1;
/**
 * Calculates legend width.
 *
 * @param {number} width - width of container in px
 * @param {number} itemWidth - width of legend item in em
 * @param {number} fontSize
 * @param {Array} data - array of chart's data, label and color
 */
function getLegendWidth(width, itemWidth, fontSize, data) {
  let columns = 0;
  // convert width to rem
  let temp = width / fontSize;
  const colWidth = itemWidth + gapWidth;
  while (temp > colWidth) {
    if (columns >= data.length) {
      break;
    }

    columns += 1;
    temp -= colWidth;
  }
  // remove final gap
  const legendStyle = {};

  if (data.length === 1) {
    legendStyle.maxWidth = `${colWidth - gapWidth}rem`;
  } else if (columns === 0) {
    legendStyle.maxWidth = width;
  } else {
    legendStyle.maxWidth = `${(colWidth * columns) - gapWidth}rem`;
  }
  return legendStyle;
}
